import React, { useState, useEffect } from 'react';
import { User, Briefcase, Users, X, Save } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';

/* ── CPF validation ────────────────────────────────────────── */
function validarCPF(cpf) {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let rem = sum % 11;
  const d1 = rem < 2 ? 0 : 11 - rem;
  if (parseInt(digits[9]) !== d1) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  rem = sum % 11;
  const d2 = rem < 2 ? 0 : 11 - rem;
  return parseInt(digits[10]) === d2;
}

/* ── eSocial categories ────────────────────────────────────── */
const CATEGORIAS_ESOCIAL = [
  { value: '101', label: '101 - Empregado Geral' },
  { value: '102', label: '102 - Empregado Rural' },
  { value: '103', label: '103 - Aprendiz' },
  { value: '104', label: '104 - Doméstico' },
  { value: '105', label: '105 - Contrato a Termo' },
  { value: '106', label: '106 - Trab. Temporário' },
  { value: '111', label: '111 - Intermitente' },
  { value: '301', label: '301 - Servidor Público' },
  { value: '401', label: '401 - Dirigente Sindical' },
  { value: '501', label: '501 - Contrib. Individual' },
  { value: '701', label: '701 - Contrib. Ind. Autônomo' },
  { value: '711', label: '711 - Estagiário' },
  { value: '721', label: '721 - Contrib. Ind. Cooperado' },
  { value: '901', label: '901 - Associação Desportiva' },
];

/* ── Form tabs config ──────────────────────────────────────── */
const FORM_TABS = [
  { id: 'pessoal',     label: 'Dados Pessoais',     icon: User },
  { id: 'contratual',  label: 'Dados Contratuais',  icon: Briefcase },
  { id: 'dependentes', label: 'Dependentes',        icon: Users },
];

/* ── Reusable form field ───────────────────────────────────── */
function FormInput({ label, tipo = 'text', valor, onChange, maxLength, spanFull }) {
  return (
    <div className={spanFull ? 'sm:col-span-2' : ''}>
      <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
        {label}
      </label>
      <input
        type={tipo}
        value={valor}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  );
}

/* ── Reusable select field ─────────────────────────────────── */
function FormSelect({ label, valor, onChange, options, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
        {label}
      </label>
      <select
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

/* ── CPF input with validation ─────────────────────────────── */
function CpfInput({ valor, onChange, disabled }) {
  const digits = valor.replace(/\D/g, '');
  const complete = digits.length === 11;
  const valid = complete && validarCPF(digits);
  const invalid = complete && !valid;

  const borderClass = valid
    ? 'border-green-500 focus:ring-green-500'
    : invalid
      ? 'border-red-500 focus:ring-red-500'
      : 'border-[color:var(--border)] focus:ring-teal-500';

  return (
    <div>
      <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
        CPF *
      </label>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={valor}
          disabled={disabled}
          maxLength={11}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
          placeholder="00000000000"
          className={`w-full px-3 py-2 pr-8 text-sm rounded-xl border bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${borderClass}`}
        />
        {complete && (
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold ${valid ? 'text-green-600' : 'text-red-500'}`}>
            {valid ? '\u2713' : '\u2717'}
          </span>
        )}
      </div>
      {invalid && (
        <p className="mt-1 text-xs text-red-500">CPF inválido. Verifique os dígitos.</p>
      )}
      {valid && (
        <p className="mt-1 text-xs text-green-600">CPF válido.</p>
      )}
    </div>
  );
}

/* ── Main form modal ───────────────────────────────────────── */
export default function FuncionarioForm({ funcionario, onSalvo, onCancelar }) {
  const [abaAtiva, setAbaAtiva] = useState('pessoal');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [cargos, setCargos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);

  const [pessoal, setPessoal] = useState({
    cpf: '',
    nome: '',
    nome_social: '',
    dt_nascimento: '',
    sexo: 'M',
    estado_civil: 'solteiro',
    pis_pasep: '',
    rg_numero: '',
    rg_orgao_emissor: '',
    telefone: '',
    email: '',
  });

  const [contratual, setContratual] = useState({
    dt_admissao: '',
    cargo_id: '',
    departamento_id: '',
    tipo_contrato: 'indeterminado',
    categoria_esocial: '101',
    tipo_regime_trab: 'clt',
    tipo_regime_prev: 'rgps',
    tipo_jornada: 'semanal',
    jornada_horas_semanais: '44',
    salario: '',
    tipo_salario: 'mensal',
  });

  const [dependentes, setDependentes] = useState([]);

  /* ── Load cargos, departamentos & populate form if editing ── */
  useEffect(() => {
    Promise.all([
      dpService.listarCargos(),
      dpService.listarDepartamentos(),
    ]).then(([cRes, dRes]) => {
      const cargosList  = cRes.data.data || [];
      const deptosList  = dRes.data.data || [];
      setCargos(cargosList);
      setDepartamentos(deptosList);

      // Match automático após listas carregadas (dados IXC)
      if (funcionario?._ixc) {
        const nomeCargo = funcionario.contratual.cargo_nome_ixc?.trim().toUpperCase();
        const nomeDepto = funcionario.contratual.departamento_nome_ixc?.trim().toUpperCase();

        const cargoMatch = cargosList.find(
          (c) => c.nome.trim().toUpperCase() === nomeCargo
        );
        const deptoMatch = deptosList.find(
          (d) => d.nome.trim().toUpperCase() === nomeDepto
        );

        setContratual((prev) => ({
          ...prev,
          cargo_id:       cargoMatch ? String(cargoMatch.id) : '',
          departamento_id: deptoMatch ? String(deptoMatch.id) : '',
        }));
      }
    }).catch(() => {});

    if (funcionario) {
      // Dados vindos do IXC via ImportarIXCModal
      if (funcionario._ixc) {
        setPessoal({ ...funcionario.pessoal });
        setContratual({ ...funcionario.contratual });
        setDependentes(funcionario.dependentes || []);
        return;
      }

      // Dados de edição normais (funcionário já cadastrado)
      const p = funcionario.pessoa || {};
      setPessoal({
        cpf: p.cpf || '',
        nome: p.nome || '',
        nome_social: p.nome_social || '',
        dt_nascimento: (p.dt_nascimento?.split('T')[0]) || '',
        sexo: p.sexo || 'M',
        estado_civil: p.estado_civil || 'solteiro',
        pis_pasep: p.pis_pasep || '',
        rg_numero: p.rg_numero || '',
        rg_orgao_emissor: p.rg_orgao_emissor || '',
        telefone: p.telefone || '',
        email: p.email || '',
      });
      setContratual({
        dt_admissao: (funcionario.dt_admissao?.split('T')[0]) || '',
        cargo_id: funcionario.cargo_id || '',
        departamento_id: funcionario.departamento_id || '',
        tipo_contrato: funcionario.tipo_contrato || 'indeterminado',
        categoria_esocial: funcionario.categoria_esocial || '101',
        tipo_regime_trab: funcionario.tipo_regime_trab || 'clt',
        tipo_regime_prev: funcionario.tipo_regime_prev || 'rgps',
        tipo_jornada: funcionario.tipo_jornada || 'semanal',
        jornada_horas_semanais: funcionario.jornada_horas_semanais || '44',
        salario: funcionario.salario || '',
        tipo_salario: funcionario.tipo_salario || 'mensal',
      });
      setDependentes(funcionario.dependentes || []);
    }
  }, [funcionario]);

  /* ── Dependente field updater ────────────────────────────── */
  const atualizarDependente = (index, field, value) => {
    setDependentes((prev) =>
      prev.map((dep, i) => (i === index ? { ...dep, [field]: value } : dep))
    );
  };

  /* ── Submit handler ──────────────────────────────────────── */
  const handleSalvar = async () => {
    if (!funcionario?.id && !validarCPF(pessoal.cpf)) {
      setErro('CPF inválido. Verifique os dígitos informados.');
      return;
    }

    setSalvando(true);
    setErro(null);

    try {
      if (funcionario?.id && !funcionario._ixc) {
        await dpService.atualizarFuncionario(funcionario.id, {
          pessoal,
          contratual,
        });
      } else {
        await dpService.criarFuncionario({
          pessoal,
          contratual,
          dependentes,
        });
      }
      onSalvo();
    } catch (err) {
      const message = err.response?.data?.message || 'Erro ao salvar funcionário';
      const errors = err.response?.data?.errors;
      setErro(errors ? Object.values(errors).flat().join(' | ') : message);
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[color:var(--surface)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--border)]">
          <h2 className="text-lg font-bold text-[color:var(--text)]">
            {funcionario?._ixc ? 'Importar do IXC' : funcionario ? 'Editar Funcionário' : 'Novo Funcionário'}
          </h2>
          <button
            onClick={onCancelar}
            className="text-[color:var(--text-muted)] hover:text-[color:var(--text)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[color:var(--border)] px-6 pt-3 gap-4">
          {FORM_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setAbaAtiva(tab.id)}
                className={`flex items-center gap-1.5 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  abaAtiva === tab.id
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text)]'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Error banner */}
          {erro && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">
              {erro}
            </div>
          )}

          {/* ── Dados Pessoais ─────────────────────────────── */}
          {abaAtiva === 'pessoal' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CpfInput
                valor={pessoal.cpf}
                onChange={(v) => setPessoal((p) => ({ ...p, cpf: v }))}
                disabled={!!funcionario?.id}
              />
              <FormInput
                label="Nome completo *"
                tipo="text"
                valor={pessoal.nome}
                onChange={(v) => setPessoal((p) => ({ ...p, nome: v }))}
              />
              <FormInput
                label="Nome social"
                tipo="text"
                valor={pessoal.nome_social}
                onChange={(v) => setPessoal((p) => ({ ...p, nome_social: v }))}
              />
              <FormInput
                label="Data de nascimento *"
                tipo="date"
                valor={pessoal.dt_nascimento}
                onChange={(v) => setPessoal((p) => ({ ...p, dt_nascimento: v }))}
              />
              <FormSelect
                label="Sexo"
                valor={pessoal.sexo}
                onChange={(v) => setPessoal((p) => ({ ...p, sexo: v }))}
                options={[
                  { value: 'M', label: 'Masculino' },
                  { value: 'F', label: 'Feminino' },
                ]}
              />
              <FormSelect
                label="Estado civil"
                valor={pessoal.estado_civil}
                onChange={(v) => setPessoal((p) => ({ ...p, estado_civil: v }))}
                options={[
                  { value: 'solteiro',      label: 'Solteiro(a)' },
                  { value: 'casado',        label: 'Casado(a)' },
                  { value: 'divorciado',    label: 'Divorciado(a)' },
                  { value: 'viuvo',         label: 'Viúvo(a)' },
                  { value: 'uniao_estavel', label: 'União Estável' },
                ]}
              />
              <FormInput
                label="PIS/PASEP"
                tipo="text"
                valor={pessoal.pis_pasep}
                maxLength={11}
                onChange={(v) => setPessoal((p) => ({ ...p, pis_pasep: v.replace(/\D/g, '') }))}
              />
              <FormInput
                label="RG"
                tipo="text"
                valor={pessoal.rg_numero}
                onChange={(v) => setPessoal((p) => ({ ...p, rg_numero: v }))}
              />
              <FormInput
                label="Órgão emissor RG"
                tipo="text"
                valor={pessoal.rg_orgao_emissor}
                onChange={(v) => setPessoal((p) => ({ ...p, rg_orgao_emissor: v }))}
              />
              <FormInput
                label="Telefone"
                tipo="tel"
                valor={pessoal.telefone}
                onChange={(v) => setPessoal((p) => ({ ...p, telefone: v }))}
              />
              <FormInput
                label="E-mail"
                tipo="email"
                valor={pessoal.email}
                spanFull
                onChange={(v) => setPessoal((p) => ({ ...p, email: v }))}
              />
            </div>
          )}

          {/* ── Dados Contratuais ──────────────────────────── */}
          {abaAtiva === 'contratual' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormInput
                label="Data de admissão *"
                tipo="date"
                valor={contratual.dt_admissao}
                onChange={(v) => setContratual((p) => ({ ...p, dt_admissao: v }))}
              />
              <FormSelect
                label="Categoria eSocial *"
                valor={contratual.categoria_esocial}
                onChange={(v) => setContratual((p) => ({ ...p, categoria_esocial: v }))}
                options={CATEGORIAS_ESOCIAL}
              />
              <div>
                <FormSelect
                  label="Cargo *"
                  valor={contratual.cargo_id}
                  onChange={(v) => setContratual((p) => ({ ...p, cargo_id: v }))}
                  options={cargos.map((c) => ({ value: c.id, label: c.nome }))}
                  placeholder="Selecione o cargo..."
                />
                {contratual.cargo_nome_ixc && (
                  <p className="mt-1 text-xs text-teal-600">
                    Cargo no IXC: <strong>{contratual.cargo_nome_ixc}</strong>
                  </p>
                )}
              </div>
              <div>
                <FormSelect
                  label="Departamento *"
                  valor={contratual.departamento_id}
                  onChange={(v) => setContratual((p) => ({ ...p, departamento_id: v }))}
                  options={departamentos.map((d) => ({ value: d.id, label: d.nome }))}
                  placeholder="Selecione o departamento..."
                />
                {contratual.departamento_nome_ixc && (
                  <p className="mt-1 text-xs text-teal-600">
                    Departamento no IXC: <strong>{contratual.departamento_nome_ixc}</strong>
                  </p>
                )}
              </div>
              <FormSelect
                label="Tipo de contrato"
                valor={contratual.tipo_contrato}
                onChange={(v) => setContratual((p) => ({ ...p, tipo_contrato: v }))}
                options={[
                  { value: 'indeterminado', label: 'Prazo Indeterminado' },
                  { value: 'determinado',   label: 'Prazo Determinado' },
                  { value: 'experiencia',   label: 'Experiência' },
                  { value: 'intermitente',  label: 'Intermitente' },
                ]}
              />
              <FormSelect
                label="Regime trabalhista"
                valor={contratual.tipo_regime_trab}
                onChange={(v) => setContratual((p) => ({ ...p, tipo_regime_trab: v }))}
                options={[
                  { value: 'clt',         label: 'CLT' },
                  { value: 'estatutario', label: 'Estatutário' },
                ]}
              />
              <FormSelect
                label="Regime previdenciário"
                valor={contratual.tipo_regime_prev}
                onChange={(v) => setContratual((p) => ({ ...p, tipo_regime_prev: v }))}
                options={[
                  { value: 'rgps', label: 'RGPS (INSS)' },
                  { value: 'rpps', label: 'RPPS' },
                  { value: 'rpc',  label: 'RPC' },
                ]}
              />
              <FormSelect
                label="Tipo de jornada"
                valor={contratual.tipo_jornada}
                onChange={(v) => setContratual((p) => ({ ...p, tipo_jornada: v }))}
                options={[
                  { value: 'semanal',  label: 'Semanal' },
                  { value: '12x36',   label: '12x36' },
                  { value: 'parcial', label: 'Parcial' },
                  { value: 'demanda', label: 'Sob Demanda' },
                ]}
              />
              <FormInput
                label="Horas semanais"
                tipo="number"
                valor={contratual.jornada_horas_semanais}
                onChange={(v) => setContratual((p) => ({ ...p, jornada_horas_semanais: v }))}
              />
              <FormInput
                label="Salário (R$) *"
                tipo="number"
                valor={contratual.salario}
                onChange={(v) => setContratual((p) => ({ ...p, salario: v }))}
              />
              <FormSelect
                label="Tipo de salário"
                valor={contratual.tipo_salario}
                onChange={(v) => setContratual((p) => ({ ...p, tipo_salario: v }))}
                options={[
                  { value: 'mensal',   label: 'Mensal' },
                  { value: 'horista',  label: 'Horista' },
                  { value: 'tarefa',   label: 'Por Tarefa' },
                  { value: 'comissao', label: 'Comissão' },
                ]}
              />
            </div>
          )}

          {/* ── Dependentes ────────────────────────────────── */}
          {abaAtiva === 'dependentes' && (
            <div className="space-y-4">
              {dependentes.length === 0 && (
                <p className="text-sm text-[color:var(--text-muted)] text-center py-4">
                  Nenhum dependente cadastrado.
                </p>
              )}

              {dependentes.map((dep, idx) => (
                <div key={idx} className="border border-[color:var(--border)] rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-[color:var(--text)]">
                      Dependente {idx + 1}
                    </span>
                    <button
                      onClick={() => setDependentes((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormInput
                      label="Nome *"
                      tipo="text"
                      valor={dep.nome}
                      onChange={(v) => atualizarDependente(idx, 'nome', v)}
                    />
                    <FormInput
                      label="CPF"
                      tipo="text"
                      valor={dep.cpf || ''}
                      maxLength={11}
                      onChange={(v) => atualizarDependente(idx, 'cpf', v.replace(/\D/g, ''))}
                    />
                    <FormInput
                      label="Data de nascimento *"
                      tipo="date"
                      valor={dep.dt_nascimento}
                      onChange={(v) => atualizarDependente(idx, 'dt_nascimento', v)}
                    />
                    <FormSelect
                      label="Tipo"
                      valor={dep.tipo_dependente}
                      onChange={(v) => atualizarDependente(idx, 'tipo_dependente', v)}
                      options={[
                        { value: 'conjuge',  label: 'Cônjuge' },
                        { value: 'filho',    label: 'Filho(a)' },
                        { value: 'enteado',  label: 'Enteado(a)' },
                        { value: 'pai_mae',  label: 'Pai/Mãe' },
                        { value: 'outro',    label: 'Outro' },
                      ]}
                    />
                    <label className="flex items-center gap-2 text-sm text-[color:var(--text)] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dep.deduz_irrf}
                        onChange={(e) => atualizarDependente(idx, 'deduz_irrf', e.target.checked)}
                        className="rounded"
                      />
                      Deduz IRRF
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[color:var(--text)] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dep.salario_familia}
                        onChange={(e) => atualizarDependente(idx, 'salario_familia', e.target.checked)}
                        className="rounded"
                      />
                      Salário-família
                    </label>
                  </div>
                </div>
              ))}

              <button
                onClick={() =>
                  setDependentes((prev) => [
                    ...prev,
                    {
                      nome: '',
                      cpf: '',
                      dt_nascimento: '',
                      tipo_dependente: 'filho',
                      deduz_irrf: false,
                      salario_familia: false,
                    },
                  ])
                }
                className="w-full py-2 text-sm border-2 border-dashed border-[color:var(--border)] rounded-xl text-[color:var(--text-muted)] hover:border-teal-500 hover:text-teal-600 transition-colors"
              >
                + Adicionar Dependente
              </button>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex gap-3 px-6 py-4 border-t border-[color:var(--border)]">
          <button
            onClick={onCancelar}
            className="flex-1 py-2 text-sm rounded-xl border border-[color:var(--border)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)] transition-colors"
          >
            Cancelar
          </button>

          {abaAtiva !== 'pessoal' && (
            <button
              onClick={() => setAbaAtiva(abaAtiva === 'dependentes' ? 'contratual' : 'pessoal')}
              className="flex-1 py-2 text-sm rounded-xl border border-[color:var(--border)] text-[color:var(--text)] hover:bg-[color:var(--surface-muted)] transition-colors"
            >
              Voltar
            </button>
          )}

          {abaAtiva !== 'dependentes' ? (
            <button
              onClick={() => setAbaAtiva(abaAtiva === 'pessoal' ? 'contratual' : 'dependentes')}
              disabled={abaAtiva === 'pessoal' && !funcionario?.id && !validarCPF(pessoal.cpf)}
              className="flex-1 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próximo
            </button>
          ) : (
            <button
              onClick={handleSalvar}
              disabled={salvando}
              className="flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 transition-colors"
            >
              {salvando ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Save size={14} />
              )}
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
